import { Close } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { LoadingButton } from '~components/atoms/custom';

type Props = DialogProps & {
  title: React.ReactNode;
  content?: React.ReactNode;
  btnAcceptText?: string;
  btnDeleteText?: string;
  btnCancelText?: string;
  onAccept?: () => void;
  onDelete?: () => void;
  onCancel?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
};

export const ConfirmationDialog: React.FC<Props> = ({
  title,
  content,
  btnAcceptText = 'Accept',
  btnDeleteText = 'Delete',
  btnCancelText = 'Cancel',
  onAccept,
  onDelete,
  onCancel,
  isLoading = false,
  disabled = false,
  ...dialogProps
}) => {
  return (
    <Dialog {...dialogProps}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingY: 1,
        }}
      >
        <Typography fontSize="16px" color="text.dark" fontWeight="600">
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={(e) =>
            dialogProps.onClose && dialogProps.onClose(e, 'escapeKeyDown')
          }
          sx={{
            color: 'text.dark',
          }}
          disabled={disabled}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={3}>
          <Typography fontSize="14px" color="text.placeholder" fontWeight="400">
            {content}
          </Typography>
          <Stack gap={1.5}>
            {onAccept && btnAcceptText && (
              <LoadingButton
                onClick={onAccept}
                isLoading={isLoading}
                disabled={disabled}
                fullWidth
                size="large"
                variant="contained"
                sx={{ borderRadius: '100px', backgroundColor: 'main' }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {btnAcceptText}
                </Typography>
              </LoadingButton>
            )}
            {onDelete && btnDeleteText && (
              <LoadingButton
                onClick={onDelete}
                isLoading={isLoading}
                disabled={disabled}
                fullWidth
                size="large"
                variant="contained"
                sx={{
                  borderRadius: '100px',
                  backgroundColor: 'red',
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {btnDeleteText}
                </Typography>
              </LoadingButton>
            )}
            {onCancel && btnCancelText && (
              <Button
                onClick={onCancel}
                disabled={disabled}
                variant="outlined"
                fullWidth
                sx={{
                  borderRadius: '100px',
                  borderColor: 'text.charcoal',
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.charcoal"
                >
                  {btnCancelText}
                </Typography>
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
